import React, { useEffect, useState } from 'react';
import {
  Image,
  StyleSheet,
  View,
  type ImageStyle,
  type StyleProp,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';
import { httpImageUri } from '../utils/imageUri';

/**
 * Shared item thumbnail — one fallback chain, one placeholder.
 *
 * Background: every surface that rendered an item thumb (Items grid,
 * Group inventory, Borrow requests, Add-to-group sheet, etc.) had its
 * own inline `{photo ? <Image> : <Placeholder>}` block. Most checked
 * only `primary_photo_url`, which doesn't exist for legacy items
 * (BE-235 backfill pending). Result: the same item rendered a photo
 * on Items and a placeholder on Group inventory. FE-256 extracts the
 * chain to one component so every surface agrees on what to show.
 *
 * Fallback order (first non-empty wins):
 *   primary_photo_url → source_image_url → image_url → item_image_url
 *
 * The component handles `onError` itself — if the chosen URL fails to
 * load (broken signed URL, network blip), it flips to the placeholder
 * rather than rendering a torn-image icon. No verbose error UI; this
 * is a thumbnail, not a hero image.
 *
 * Sizing: pass `size` for a fixed square (GroupInventorySection 44px,
 * NotificationCard 36px, etc.) — that's the common case. Omit `size`
 * and pass a `style` with explicit width/height (or 100%/100%) when
 * the thumb should fill its parent (BrowseScreen ItemGridCard does
 * this).
 */

export interface ItemThumbnailItem {
  primary_photo_url?: string | null;
  source_image_url?: string | null;
  image_url?: string | null;
  item_image_url?: string | null;
}

interface ItemThumbnailProps {
  item: ItemThumbnailItem;
  /** Fixed square edge in px. Omit to let `style` drive sizing. */
  size?: number;
  shape?: 'square' | 'circle';
  /** MaterialIcons glyph rendered when no URL resolves. Default: 'image'. */
  placeholderIcon?: keyof typeof MaterialIcons.glyphMap;
  style?: StyleProp<ImageStyle>;
  testID?: string;
}

const pickUrl = (item: ItemThumbnailItem): string | null => {
  // backend#364: a freshly-generated cutout can transiently arrive as a
  // raw `gs://…` path (and a few legacy rows are malformed). Skip any
  // non-http(s) candidate so we fall through to a loadable URL (e.g. the
  // signed source_image_url) instead of erroring out to the placeholder
  // when a perfectly good image is available further down the chain.
  return (
    httpImageUri(
      item.primary_photo_url,
      item.source_image_url,
      item.image_url,
      item.item_image_url,
    ) ?? null
  );
};

export const ItemThumbnail: React.FC<ItemThumbnailProps> = ({
  item,
  size,
  shape = 'square',
  placeholderIcon = 'image',
  style,
  testID,
}) => {
  const uri = pickUrl(item);
  const [errored, setErrored] = useState(false);

  // Reset the error state when the URL changes — common when a list
  // refetches and the same row gets a fresher signed URL.
  useEffect(() => {
    setErrored(false);
  }, [uri]);

  const sized: ImageStyle | undefined =
    typeof size === 'number'
      ? {
          width: size,
          height: size,
          borderRadius: shape === 'circle' ? size / 2 : 8,
        }
      : undefined;

  if (!uri || errored) {
    // Icon size scales with the thumb when `size` is known; otherwise
    // a sensible default (22) that reads well at most viewport sizes.
    const iconSize =
      typeof size === 'number' ? Math.max(14, Math.round(size * 0.42)) : 22;
    return (
      <View
        style={[sized, styles.placeholder, style]}
        testID={testID}
      >
        <MaterialIcons
          name={placeholderIcon}
          size={iconSize}
          color={fpColors.outline}
        />
      </View>
    );
  }

  // Image fit sweep (2026-06-23): default to `contain` so the WHOLE
  // image shows in the card, even if its aspect ratio doesn't match
  // the card's. The tradeoff is letterbox bars when ratios differ —
  // those bars get a subtly contrasting background tint so the user
  // reads them as "card padding," not "the image's own background."
  // Photo carousel inside ItemDetail (where the user can pinch-zoom
  // the full image) intentionally keeps `cover` and does NOT use this
  // component.
  return (
    <Image
      source={{ uri }}
      style={[styles.imageLetterbox, sized, style]}
      resizeMode="contain"
      onError={() => setErrored(true)}
      accessibilityIgnoresInvertColors
      testID={testID}
    />
  );
};

const styles = StyleSheet.create({
  // FE #153 — strengthened placeholder contrast. Previously the
  // background was `surfaceContainerLow` which read nearly identical
  // to the page bg, so on rows that had a placeholder (legacy items
  // without primary_photo_url) the thumbnail slot looked EMPTY —
  // creating a visual rhythm break between rows-with-photo and
  // rows-without. Switched to `surfaceContainer` (darker) + added a
  // 1px outline so the slot is always visibly present.
  placeholder: {
    backgroundColor: fpColors.surfaceContainer,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  // Subtle tint behind the contained image so the letterbox bars
  // read as "card padding" rather than part of the photo. Matches
  // the placeholder bg tone family so the slot looks consistent
  // whether the image is loaded or not.
  imageLetterbox: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
});

export default ItemThumbnail;
